import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, ArrowRight, BookOpen } from 'lucide-react';
import { fetchBlogPosts, resolveImageUrl } from '../lib/travelApi';

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts()
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section data-testid="blog-page" className="min-h-screen py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-amber-600 font-semibold uppercase tracking-wider text-sm">Travel Blog</span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">Stories & Travel Tips</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Inspiration, guides, and insider tips to help you plan your next unforgettable journey.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-gray-500">Loading articles...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No blog posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {posts.map((post) => (
              <Card
                key={post.id}
                data-testid={`blog-card-${post.slug}`}
                onClick={() => navigate(`/blog/${post.slug}`)}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer flex flex-col"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={resolveImageUrl(post.cover_image)}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6 flex flex-col flex-1">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <Calendar className="w-4 h-4 mr-2 text-amber-600" />
                    <span>{formatDate(post.date)}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-amber-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">{post.excerpt}</p>
                  <div className="flex items-center text-amber-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Read More <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogPage;
